/**
 * The lost-race prompt: what the other person changed, and the two things the trainer may do about
 * it.
 *
 * ── Exactly two actions, and the missing third is structural ─────────────────────────────────
 * Document-model ADR §4. A save is whole-tree replace under an opaque `baseVersion`, so a "save mine
 * anyway" would re-send the tree as it stood BEFORE the winner's edit landed — erasing that edit,
 * and resurrecting any row it deleted, because the tree still carries that row's slot token. That is
 * a property of the save protocol rather than a feature nobody got round to, so this component has
 * no third button and no prop that could grow one. Its interface admits two callbacks.
 *
 * ── `AlertDialog`, not `Dialog` ──────────────────────────────────────────────────────────────
 * An alert dialog cannot be dismissed by clicking the backdrop or pressing Escape into nothing — the
 * trainer must choose. That is right here: dismissing by accident leaves the document in a state
 * whose next save fails identically, with no explanation of why. Base UI supplies the focus trap,
 * the `alertdialog` role, the labelling wiring and the return of focus; every visual byte is ours.
 *
 * ── Every byte is ours, and that is not a style choice ───────────────────────────────────────
 * `main.tsx` wraps the app in `<CSPProvider disableStyleElements>`, because the production policy's
 * `style-src-elem 'self'` blocks an injected inline `<style>` outright — and the failure is
 * invisible in development, where a per-process nonce is minted. So Base UI ships no CSS at all: an
 * unstyled part here renders as text floating over the page rather than as an error. Positioning is
 * unaffected, because Base UI positions through inline `style` attributes.
 *
 * ── The words arrive worded ──────────────────────────────────────────────────────────────────
 * Every string is on the prompt. This component holds no `t`, no arithmetic and no decision about
 * which change matters — `useSavePlanDocument` composes the list from `summariseConflict`, which is
 * where a test can reach it. What is left here is a heading, a description, a list and two buttons.
 */

import { AlertDialog } from '@base-ui/react/alert-dialog';

import type { ConflictPrompt } from '../lib/useSavePlanDocument';

import styles from './ConflictDialog.module.css';

interface ConflictDialogProps {
  /** `null` closes it. The prompt exists exactly while the trainer is being asked. */
  readonly prompt: ConflictPrompt | null;
  /** Take the server's tree. The local edits are discarded — the list above says what for. */
  readonly onAdopt: () => void;
  /** Keep editing. The token stays stale, so the next save conflicts again. */
  readonly onKeepEditing: () => void;
}

export function ConflictDialog({ prompt, onAdopt, onKeepEditing }: ConflictDialogProps) {
  return (
    <AlertDialog.Root
      open={prompt !== null}
      onOpenChange={(open) => {
        // An alert dialog only closes through its own actions, so this fires for the Escape key.
        // Escape means "not now", which is the keep-editing outcome — never the destructive one.
        if (!open) onKeepEditing();
      }}
    >
      <AlertDialog.Portal>
        <AlertDialog.Backdrop className={styles.backdrop} />
        <AlertDialog.Popup className={styles.popup}>
          <AlertDialog.Title className={styles.title}>{prompt?.title}</AlertDialog.Title>
          <AlertDialog.Description className={styles.description}>
            {prompt?.description}
          </AlertDialog.Description>

          <ul className={styles.changes}>
            {prompt?.lines.map((line) => (
              // The line IS the identity — two identically-worded changes are two changes to the
              // same-named node, which the trainer reads as one fact stated once.
              <li key={line} className={styles.change}>
                {line}
              </li>
            ))}
          </ul>
          {prompt?.overflow != null && <p className={styles.overflow}>{prompt.overflow}</p>}

          <div className={styles.actions}>
            {/* Keep editing comes first in the DOM and is the default focus, because it is the
              * non-destructive one: a trainer who presses Enter on reflex keeps their work. */}
            <button type="button" className={styles.ghost} onClick={onKeepEditing}>
              {prompt?.keepEditingLabel}
            </button>
            <button type="button" className={styles.danger} onClick={onAdopt}>
              {prompt?.adoptLabel}
            </button>
          </div>
        </AlertDialog.Popup>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
